import {
  Alert,
  Button,
  Flex,
  InputNumber,
  Modal,
  Skeleton,
  Typography,
  message,
} from 'antd';
import { FC, useEffect, useState } from 'react';

import { useAdminAccountingUsageController_getPlan } from '@api-client/generated/AdminAccountingUsageController/getPlan';
import { useAdminAccountingUsageController_updatePlan } from '@api-client/generated/AdminAccountingUsageController/updatePlan';
import { Schemas } from '@api-client/generated/types';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { getApiErrorMessage } from '@utils';

import {
  MAX_PLAN_LIMIT,
  PLAN_TYPE_LABEL,
  PlanType,
  STATUS_COLOR,
  formatDate,
  formatInteger,
  formatPlanAmount,
  statusFor,
} from './format';

const { Text } = Typography;

/** What the company has used this year, both metrics, so the preview can follow a type switch. */
export type AccountingPlanUsage = {
  bookableDocuments: number;
  turnover: number;
};

type Props = {
  companyId: string;
  companyName: string;
  /** This year's usage. Omitted where it is not at hand (the company's Service section): no preview then. */
  usage?: AccountingPlanUsage;
  onClose: () => void;
};

const TYPE_HINT: Record<PlanType, string> = {
  documents: 'Limit in bookable documents',
  legacy_turnover: 'Limit in € turnover per year',
};

/** The backend's 400 code for a move onto a legacy plan, in words a CSM can act on. */
const SAVE_ERRORS: Record<string, string> = {
  plan_type_change_not_allowed:
    'This company can no longer be put on a legacy turnover plan — it may have been moved to Bookings meanwhile.',
};

const TypeCard = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: 10px 12px;
  text-align: left;
  border-radius: 10px;
  cursor: pointer;
  border: ${({ selected }) =>
    selected ? '2px solid #1a56db' : '1px solid #d5dbe7'};
  background: ${({ selected }) => (selected ? '#eef3ff' : '#fff')};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const History = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 160px;
  overflow-y: auto;
  font-size: 12px;
`;

const describeChange = ({
  attribute,
  before,
  after,
}: Schemas.AccountingPlanFieldChangeDto): string => {
  const show = (value: string | null) =>
    value === null
      ? '—'
      : attribute === 'planType'
        ? (PLAN_TYPE_LABEL[value as PlanType] ?? value)
        : formatInteger(Number(value));
  const label = attribute === 'planType' ? 'type' : 'limit';
  return before === null
    ? `${label} ${show(after)}`
    : `${label} ${show(before)} → ${show(after)}`;
};

const authorOf = (
  author: Schemas.AccountingPlanChangeAuthorDto | null
): string => {
  if (!author) return 'unknown';
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ');
  return name || author.email;
};

/**
 * "Edit plan" — always the company's current plan (the API has no per-year plan to edit; past years
 * are read from history). The type only moves towards Bookings: Legacy turnover is offered only to a
 * company already on it, and leaving it is permanent, which the popup says before Save. Switching
 * the type clears the limit — the CSM sets the new one, there is no default.
 *
 * Mounted only while open, so it seeds its form once from the first plan it loads; a background
 * refetch must not overwrite what the CSM is typing.
 */
const AccountingPlanModal: FC<Props> = ({
  companyId,
  companyName,
  usage,
  onClose,
}) => {
  const {
    data: plan,
    error,
    isFetchedAfterMount,
    dataUpdatedAt,
  } = useAdminAccountingUsageController_getPlan({ params: { companyId } });
  const { mutate: updatePlan, isPending: isSaving } =
    useAdminAccountingUsageController_updatePlan();
  const queryClient = useQueryClient();

  const [planType, setPlanType] = useState<PlanType>('documents');
  const [planLimit, setPlanLimit] = useState<number | null>(null);
  // The form is seeded once, from a plan fetched after the popup opened — never from a cached one
  // that another CSM may have changed since — and a background refetch must not overwrite what the
  // CSM is typing. After a refused save it waits for a response newer than the refused one.
  const [seeded, setSeeded] = useState(false);
  const [staleBefore, setStaleBefore] = useState(0);

  useEffect(() => {
    if (plan && isFetchedAfterMount && !seeded && dataUpdatedAt > staleBefore) {
      setSeeded(true);
      setPlanType(plan.planType ?? 'documents');
      setPlanLimit(plan.planLimit);
    }
  }, [plan, isFetchedAfterMount, seeded, dataUpdatedAt, staleBefore]);

  const onLegacy = plan?.planType === 'legacy_turnover';
  const leavingLegacy = onLegacy && planType !== 'legacy_turnover';
  const unchanged =
    plan?.planType === planType && plan?.planLimit === planLimit;

  const choose = (type: PlanType) => {
    if (type === planType) return;
    setPlanType(type);
    setPlanLimit(null);
  };

  const invalidate = () => {
    // Both readers of the plan: the popup's own history and every year of the usage table.
    void queryClient.invalidateQueries({
      queryKey: ['AdminAccountingUsageController_getPlan'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['AdminAccountingUsageController_list'],
    });
  };

  const save = () => {
    if (!planLimit) return;

    updatePlan(
      { parameter: { companyId }, requestBody: { planType, planLimit } },
      {
        onSuccess: () => {
          message.success('Plan saved');
          invalidate();
          onClose();
        },
        onError: (saveError) => {
          const text = getApiErrorMessage(saveError, 'Could not save the plan');
          message.error(SAVE_ERRORS[text] ?? text);
          // The plan this popup was opened on is out of date: refetch it and re-seed the form from it.
          setStaleBefore(dataUpdatedAt);
          setSeeded(false);
          invalidate();
        },
      }
    );
  };

  const used = usage
    ? planType === 'legacy_turnover'
      ? usage.turnover
      : usage.bookableDocuments
    : null;
  const percent =
    used !== null && planLimit ? Math.round((used / planLimit) * 100) : null;

  return (
    <Modal
      open
      width={440}
      centered
      title={
        <Flex vertical>
          <span>Edit plan</span>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            {companyName}
          </Text>
        </Flex>
      }
      onCancel={onClose}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isSaving}
            disabled={!planLimit || unchanged}
            onClick={save}
          >
            Save changes
          </Button>
        </Flex>
      }
    >
      {/* An error only replaces a form that was never filled: a failed background refetch keeps it. */}
      {error && !seeded ? (
        <Alert
          type="error"
          showIcon
          message={getApiErrorMessage(error, 'Could not load the plan')}
        />
      ) : !seeded ? (
        <Skeleton active />
      ) : (
        <Flex gap={16} vertical>
          <Flex gap={8} role="radiogroup" aria-label="Plan type">
            {(['documents', 'legacy_turnover'] as const).map((type) => (
              <TypeCard
                key={type}
                type="button"
                role="radio"
                aria-checked={planType === type}
                selected={planType === type}
                disabled={type === 'legacy_turnover' && !onLegacy}
                title={
                  type === 'legacy_turnover' && !onLegacy
                    ? 'Legacy turnover plans are grandfathered: a company can only move off one'
                    : undefined
                }
                onClick={() => choose(type)}
              >
                <Text strong>{PLAN_TYPE_LABEL[type]}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {TYPE_HINT[type]}
                </Text>
              </TypeCard>
            ))}
          </Flex>

          {leavingLegacy && (
            <Alert
              type="warning"
              showIcon
              message="Moving to Bookings is permanent: a legacy turnover plan cannot be given back."
            />
          )}

          <Flex gap={4} vertical>
            <Text strong>Plan limit · per year</Text>
            <InputNumber
              aria-label="Plan limit"
              style={{ width: '100%' }}
              min={1}
              max={MAX_PLAN_LIMIT}
              precision={0}
              controls={false}
              value={planLimit}
              onChange={(value) =>
                setPlanLimit(value === null ? null : Number(value))
              }
              addonAfter={planType === 'legacy_turnover' ? '€' : 'docs'}
            />
          </Flex>

          {used !== null && percent !== null && planLimit && (
            <Text>
              Current usage {formatPlanAmount(used, planType)} →{' '}
              <Text
                strong
                style={{ color: STATUS_COLOR[statusFor(used, planLimit)] }}
              >
                {percent}% of new limit
              </Text>
            </Text>
          )}

          <Flex gap={4} vertical>
            <Text strong>History</Text>
            {plan?.history.length ? (
              <History>
                {plan.history.map((change) => (
                  <li key={change.id}>
                    <Text type="secondary">
                      {formatDate(change.changedAt)} ·{' '}
                      {authorOf(change.changedBy)}:
                    </Text>{' '}
                    {change.changes.map(describeChange).join(', ')}
                  </li>
                ))}
              </History>
            ) : (
              <Text type="secondary">
                No changes made in the backoffice yet.
              </Text>
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};

export default AccountingPlanModal;
